<!-- ClinicSwitcher — dropdown para alternar entre clínicas -->
<ng-container *ngIf="isVisible">

  <!-- Loading -->
  <div *ngIf="loading && activeClinic?.cnpj" class="flex items-center gap-2 px-[12px] py-[10px] rounded-[10px] border border-westetics-gray-200 text-xs text-westetics-neutral-500 bg-westetics-neutral-50">
    <svg class="w-4 h-4 text-westetics-purple-400 flex-shrink-0 animate-pulse" viewBox="0 0 20 20" fill="currentColor"><path d="M10 2L3 7V18H8V12H12V18H17V7L10 2Z"/></svg>
    <span class="text-left font-medium break-words">{{ clinicDisplayName(activeClinic) }}</span>
    <svg class="w-3 h-3 ml-auto animate-spin text-westetics-purple-400 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
    </svg>
  </div>

  <!-- Apenas 1 clínica (texto estático, sem dropdown) -->
  <div *ngIf="!loading && !hasMultipleClinics" class="flex items-center gap-2 px-[12px] py-[10px] text-xs text-westetics-neutral-800">
    <svg class="w-4 h-4 text-westetics-purple-400 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor"><path d="M10 2L3 7V18H8V12H12V18H17V7L10 2Z"/></svg>
    <span class="text-left font-medium break-words">{{ clinicDisplayName(activeClinic) }}</span>
  </div>

  <!-- Dropdown -->
  <div *ngIf="!loading && hasMultipleClinics" class="relative">
    <!-- Trigger -->
    <button
      type="button"
      (click)="toggleDropdown()"
      class="w-full flex items-center justify-between gap-2 px-[12px] py-[10px] rounded-[10px] border text-xs bg-white hover:bg-westetics-neutral-50 transition-colors"
      [ngClass]="{
        'border-westetics-purple-800 text-westetics-neutral-800': !isRootMode,
        'border-amber-500 text-amber-700': isRootMode
      }"
      aria-haspopup="listbox"
      [attr.aria-expanded]="dropdownOpen"
    >
      <div class="flex items-center gap-2 min-w-0">
        <svg *ngIf="isRootMode" class="w-4 h-4 text-amber-500 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor"><path d="M10 1L13 7L19 8L14.5 12.5L15.5 19L10 16L4.5 19L5.5 12.5L1 8L7 7L10 1Z"/></svg>
        <svg *ngIf="!isRootMode" class="w-4 h-4 text-westetics-purple-400 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor"><path d="M10 2L3 7V18H8V12H12V18H17V7L10 2Z"/></svg>
        <span class="text-left font-medium break-words">{{ triggerLabel }}</span>
      </div>
      <svg
        class="w-4 h-4 text-westetics-gray-600 flex-shrink-0 transition-transform duration-200"
        [class.rotate-180]="dropdownOpen"
        viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"
      >
        <path d="M9.99956 10.879L13.7121 7.1665L14.7726 8.227L9.99956 13L5.22656 8.227L6.28706 7.1665L9.99956 10.879Z" fill="#99A0AE"/>
      </svg>
    </button>

    <!-- Panel -->
    <div *ngIf="dropdownOpen" class="absolute z-50 left-0 bottom-full mb-2 w-full bg-white border border-westetics-gray-200 rounded-lg shadow-lg overflow-hidden">

      <!-- Busca (quando >5 clínicas ou ROOT) -->
      <div *ngIf="showSearch" class="px-3 py-2 border-b border-westetics-gray-100">
        <input
          type="text"
          [ngModel]="searchTerm"
          (ngModelChange)="searchTerm = $event"
          placeholder="Buscar clínica..."
          class="w-full px-2 py-1.5 text-xs border border-westetics-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-westetics-purple-400 focus:border-westetics-purple-400"
          (click)="$event.stopPropagation()"
        />
      </div>

      <ul role="listbox" class="max-h-56 overflow-auto py-1">
        <!-- Opção ROOT -->
        <li *ngIf="isRoot" role="option" (click)="selectRoot()"
          class="px-3 py-2 cursor-pointer text-xs flex items-center gap-2 transition-colors font-semibold"
          [ngClass]="{
            'bg-amber-100 text-amber-800': isRootMode,
            'hover:bg-amber-50 text-amber-700': !isRootMode
          }"
        >
          <svg class="w-4 h-4 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor"><path d="M10 1L13 7L19 8L14.5 12.5L15.5 19L10 16L4.5 19L5.5 12.5L1 8L7 7L10 1Z"/></svg>
          <span>ROOT</span>
        </li>

        <!-- Separator -->
        <li *ngIf="isRoot && filteredClinics.length > 0" class="border-b border-westetics-gray-100 my-1"></li>

        <!-- Clínicas -->
        <li *ngFor="let clinic of filteredClinics" role="option" (click)="selectClinic(clinic)"
          class="px-3 py-2 cursor-pointer text-xs flex items-center gap-2 transition-colors"
          [ngClass]="{
            'bg-westetics-purple-600 text-white': isActive(clinic),
            'hover:bg-westetics-purple-50 text-westetics-neutral-800': !isActive(clinic)
          }"
        >
          <svg class="w-4 h-4 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor"><path d="M10 2L3 7V18H8V12H12V18H17V7L10 2Z"/></svg>
          <span class="break-words leading-tight">{{ clinicDisplayName(clinic) }}</span>
        </li>

        <!-- Nenhum resultado -->
        <li *ngIf="filteredClinics.length === 0" class="px-3 py-3 text-xs text-center text-westetics-neutral-400">
          Nenhuma clínica encontrada.
        </li>
      </ul>
    </div>
  </div>

</ng-container>
